import { useRef } from "react";
import { motion, useScroll } from "framer-motion";
import { FadeUp, MaskedLine, MonoLabel } from "@/components/Reveal";

const steps = [
  {
    num: "01",
    title: "Listen",
    body: "First I dig into how Google and AI tools see you right now. What works, what's broken, and what your competitors are doing better. You get an honest picture of where you stand.",
  },
  {
    num: "02",
    title: "Map",
    body: "Next I pin down the exact questions your buyers ask, on Google and to AI. Then I build the plan to make you the answer.",
  },
  {
    num: "03",
    title: "Build",
    body: "Then I get to work. Content, technical fixes and PR, shipped in short sprints. You see progress every week. No black box.",
  },
  {
    num: "04",
    title: "Compound",
    body: "Being found adds up over time. I track your rankings and AI mentions every month, do more of what works, and turn early wins into a lead competitors can't catch.",
  },
];

export default function Process() {
  const stepsRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: stepsRef,
    offset: ["start 0.75", "end 0.55"],
  });

  return (
    <section id="process" className="border-t border-black/10 px-6 sm:px-12 py-32 sm:py-40">
      <div className="grid grid-cols-12 gap-8 lg:gap-12">
        <div className="col-span-12 lg:col-span-4">
          <div className="lg:sticky lg:top-32">
            <FadeUp>
              <MonoLabel>The process</MonoLabel>
            </FadeUp>
            <h2 className="mt-6 font-display font-semibold text-4xl sm:text-5xl tracking-[-0.03em] leading-[1.06]">
              <MaskedLine>The way</MaskedLine>
              <MaskedLine delay={0.1}>
                <span className="text-gradient">I work.</span>
              </MaskedLine>
            </h2>
            <FadeUp delay={0.2}>
              <p className="mt-8 text-muted-foreground text-base leading-relaxed max-w-sm">
                Every project runs on the same four steps. I listen first,
                then I plan, then I build, and I stay until the results show
                up. Scroll on and watch the line draw itself.
              </p>
            </FadeUp>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-7 lg:col-start-6">
          <div ref={stepsRef} className="relative pl-10 sm:pl-14">
            <div className="absolute left-[7px] sm:left-[9px] top-1 bottom-1 w-px bg-black/10" />
            <motion.div
              style={{ scaleY: scrollYProgress }}
              className="absolute left-[7px] sm:left-[9px] top-1 bottom-1 w-px origin-top bg-gradient-to-b from-[#0071e3] via-[#6a5cff] to-[#c644fc]"
              data-testid="process-progress-line"
            />
            {steps.map((s, i) => (
              <div
                key={s.num}
                className="relative py-10 sm:py-14 first:pt-0 last:pb-0"
                data-testid={`process-step-${s.num}`}
              >
                <motion.span
                  initial={{
                    backgroundColor: "#ffffff",
                    borderColor: "rgba(0,0,0,0.2)",
                    boxShadow: "0 0 0 0 rgba(0,113,227,0)",
                  }}
                  whileInView={{
                    backgroundColor: "#0071e3",
                    borderColor: "#0071e3",
                    boxShadow: "0 0 24px 4px rgba(0,113,227,0.35)",
                  }}
                  viewport={{ once: true, margin: "-35%" }}
                  transition={{ duration: 0.5 }}
                  style={i === 0 ? { top: "8px" } : undefined}
                  className="absolute -left-[39px] sm:-left-[55px] top-12 sm:top-[4.25rem] w-[13px] h-[13px] rounded-full border-2"
                />
                <FadeUp>
                  <div className="flex items-baseline gap-4">
                    <span className="font-mono text-sm text-primary">{s.num}</span>
                    <h3 className="font-display font-semibold text-2xl sm:text-4xl tracking-[-0.02em]">
                      {s.title}
                    </h3>
                  </div>
                  <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl">
                    {s.body}
                  </p>
                </FadeUp>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
